import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Grid, List, SlidersHorizontal, Clock, Phone, MapPin, Calendar, CheckCircle, Users } from 'lucide-react';
import { useTrucks } from '../hooks/useTrucks';
import { LoadingCard, LoadingPage } from '../components/LoadingSpinner';
import SEO from '../components/SEO';

const VehiclesForHirePage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [hireDuration, setHireDuration] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  const apiFilters = useMemo(() => {
    const filters = {};
    if (searchTerm) filters.search = searchTerm;
    if (selectedMake) filters.make = selectedMake;
    return filters;
  }, [searchTerm, selectedMake]);

  const { trucks: allTrucks, loading, error } = useTrucks(apiFilters);

  // Filter trucks available for hire and apply additional filters
  const hireVehicles = useMemo(() => {
    let filtered = allTrucks.filter(truck => truck.available_for_hire === true);
    
    if (selectedType) {
      filtered = filtered.filter(truck => 
        truck.bodytype && truck.bodytype.toLowerCase().includes(selectedType.toLowerCase())
      );
    }

    return filtered;
  }, [allTrucks, selectedType]);

  const makes = [...new Set(hireVehicles.map(truck => truck.make))].sort();

  const HireVehicleCard = ({ truck, isListView = false }) => (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all group ${
      isListView ? 'flex' : ''
    }`}>
      <div className={`relative ${isListView ? 'w-1/3' : ''}`}>
        <img 
          src="https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
          alt={truck.title}
          className={`object-cover group-hover:scale-105 transition-transform duration-300 ${
            isListView ? 'w-full h-full' : 'w-full h-48'
          }`}
        />
        <div className="absolute top-3 right-3 bg-orange-500/80 backdrop-blur-sm border border-orange-400/30 text-white px-3 py-1 rounded-lg text-sm font-bold shadow-xl">
          {truck.hire_status === 'available' ? 'Hire Fleet' : 
           truck.hire_status === 'hired' ? 'Currently Hired' : 'Under Maintenance'}
        </div>
        <div className={`absolute top-3 left-3 text-white px-2 py-1 rounded-lg text-xs font-bold shadow-xl backdrop-blur-sm border ${
          truck.hire_status === 'available' ? 'bg-green-500/80 border-green-400/30' : 
          truck.hire_status === 'hired' ? 'bg-red-500/80 border-red-400/30' : 'bg-yellow-500/80 border-yellow-400/30'
        }`}>
          <CheckCircle className="h-3 w-3 inline mr-1" />
          {truck.hire_status === 'available' ? 'Ready' : 
           truck.hire_status === 'hired' ? 'Hired' : 'Maintenance'}
        </div>
      </div>
      <div className={`p-6 ${isListView ? 'flex-1' : ''}`}>
        <h3 className="text-xl font-bold text-black mb-2 font-inter line-clamp-2">{truck.title}</h3>
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          <span>{truck.year}</span>
          <span>•</span>
          <span>{truck.bodytype}</span>
          <span>•</span>
          <span>{truck.power}</span>
        </div>

        {isListView && (
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div><strong>Type:</strong> {truck.bodytype}</div>
            <div><strong>Weight:</strong> {truck.weight}t</div>
            <div><strong>Emissions:</strong> {truck.emissions}</div>
            <div><strong>Transmission:</strong> Manual</div>
          </div>
        )}

        <div className="bg-orange-50 p-3 rounded-lg mb-4">
          <div className="text-sm text-gray-600 mb-2">Hire Rates</div>
          <div className="grid grid-cols-3 gap-4 text-center">
            {truck.hire_daily_rate && (
              <div>
                <div className="text-lg font-bold text-black">£{truck.hire_daily_rate}</div>
                <div className="text-sm text-gray-500">Daily rate</div>
              </div>
            )}
            {truck.hire_weekly_rate && (
              <div>
                <div className="text-lg font-bold text-black">£{truck.hire_weekly_rate}</div>
                <div className="text-sm text-gray-500">Weekly rate</div>
              </div>
            )}
            {truck.hire_monthly_rate && (
              <div>
                <div className="text-lg font-bold text-black">£{truck.hire_monthly_rate}</div>
                <div className="text-sm text-gray-500">Monthly rate</div>
              </div>
            )}
          </div>
          {!truck.hire_daily_rate && !truck.hire_weekly_rate && !truck.hire_monthly_rate && (
            <div className="text-center text-gray-600">
              <p>Rates available on request</p>
            </div>
          )}
        </div>
        
        <div className="flex gap-2">
          <a
            href={truck.hire_status === 'available' ? 'tel:01530231235' : '#'}
            className={`flex-1 px-4 py-2 rounded-lg font-bold transition-colors text-sm flex items-center justify-center ${
              truck.hire_status === 'available' 
                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                : 'bg-gray-400 text-white cursor-not-allowed'
            }`}
            onClick={(e) => truck.hire_status !== 'available' && e.preventDefault()}
          >
            <Phone className="h-4 w-4 inline mr-2" />
            {truck.hire_status === 'available' ? 'Call to Book' : 'Not Available'}
          </a>
          <Link 
            to={`/trucks/${truck.id}`}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-bold transition-colors text-center text-sm"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );

  if (loading && hireVehicles.length === 0) {
    return <LoadingPage message="Loading vehicles for hire..." />;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="Vehicles for Hire"
        description="Professional MAN truck hire services in Leicester. Short-term and long-term commercial vehicle rental. Quality maintained trucks for all your transport needs. Call 01530 231 235 to book."
        keywords="MAN truck hire Leicester, commercial vehicle rental, truck hire services, short term truck rental, long term vehicle hire"
        canonical="/vehicles-for-hire"
      />
      
      {/* Header */}
      <div className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-orange-600 rounded-full p-4">
                <Clock className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black font-inter mb-4">
              Vehicles for <span className="text-orange-500">Hire</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-inter">
              Professional commercial vehicle hire solutions. Daily, weekly, and monthly rental options available with flexible terms and competitive rates.
            </p>
            <div className="flex justify-center items-center gap-8 mt-8 text-gray-300">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Fully Maintained</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Insurance Available</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Contact */}
        <div className="bg-orange-600 rounded-lg p-6 mb-8 text-white">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-2">Need a Quote Fast?</h2>
              <p className="text-orange-100">Call our hire specialists for instant pricing and availability</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-black">01530 231 235</div>
                <div className="text-sm text-orange-200">Office Hours</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black">07718 738 785</div>
                <div className="text-sm text-orange-200">24/7 Emergency</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Search Available Hire Vehicles</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by make, model, vehicle type, or specifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
              />
            </div>
          </div>

          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded transition-colors"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-inter">View:</span>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Make</label>
                <select
                  value={selectedMake}
                  onChange={(e) => setSelectedMake(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">All Makes</option>
                  {makes.map(make => (
                    <option key={make} value={make}>{make}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Vehicle Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">All Types</option>
                  <option value="tractor">Tractor Units</option>
                  <option value="rigid">Rigid Vehicles</option>
                  <option value="chassis">Chassis Cabs</option>
                  <option value="curtain">Curtainsides</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Hire Duration</label>
                <select
                  value={hireDuration}
                  onChange={(e) => setHireDuration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">Any Duration</option>
                  <option value="daily">Daily Hire</option>
                  <option value="weekly">Weekly Hire</option>
                  <option value="monthly">Monthly Hire</option>
                  <option value="long-term">Long Term Hire</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedMake('');
                    setSelectedType('');
                    setHireDuration('');
                  }}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded font-bold transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600 font-inter">
            Showing {hireVehicles.length} vehicle{hireVehicles.length !== 1 ? 's' : ''} available for hire
            {loading && ' (loading...)'}
          </p>
          {error && (
            <p className="text-red-600 font-inter mt-2">{error}</p>
          )}
        </div>

        {/* Vehicles Grid/List */}
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
            : 'space-y-6'
        }>
          {loading && hireVehicles.length === 0 ? (
            Array.from({ length: 6 }).map((_, index) => (
              <LoadingCard key={index} />
            ))
          ) : (
            hireVehicles.map((truck) => (
              <HireVehicleCard key={truck.id} truck={truck} isListView={viewMode === 'list'} />
            ))
          )}
        </div>

        {!loading && hireVehicles.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🚚</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2 font-inter">No vehicles currently available for hire</h3>
            <p className="text-gray-600 font-inter">
              Please call us on <span className="font-bold text-orange-600">01530 231 235</span> to discuss your hire requirements
            </p>
          </div>
        )}

        {/* Why Choose Our Hire Service */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-black text-black mb-6 font-inter text-center">
            Why Choose Our <span className="text-orange-600">Hire Service?</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Flexible Terms</h3>
              <p className="text-gray-600 font-inter">Daily, weekly, monthly or long-term hire available to suit your needs.</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Fully Maintained</h3>
              <p className="text-gray-600 font-inter">All vehicles serviced and maintained to the highest standards.</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Expert Support</h3>
              <p className="text-gray-600 font-inter">24/7 support and breakdown assistance included.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehiclesForHirePage;